import React, { useState, useEffect } from 'react';
import { getAllSyncedData } from '../services/firestoreService';
import { getWineRecommendation } from '../services/geminiService';
import type { AppData, Wine, UserAnswers, AiRecommendation } from '../types';
import Questionnaire from './Questionnaire';
import GlassWineQuestionnaire from './GlassWineQuestionnaire';
import Recommendation from './Recommendation';
import LoadingSpinner from './LoadingSpinner';
import QRCode from 'qrcode';
import { useTheme } from '../src/hooks/useTheme';
import PrintIcon from './icons/PrintIcon';
import { defaultPdfStyle, glassPdfStyle, generateWineListPdf } from '../utils/wineListPdfGenerator';

// 一時的にインライン定義
const SommelierCatIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    className={className}
    aria-label="Cat Paw Icon"
    role="img"
    fill="currentColor"
  >
    <path d="M12 18.2c-1.45 2.08-4.5 2.08-5.95 0C4.6 16.12 6.55 13 9.1 13.6c.85.2 1.8.95 2.9 1.95c1.1-.99 2.05-1.75 2.9-1.95c2.55-.6 4.5 2.52 3.05 4.6c-1.45 2.08-4.5 2.08-5.95 0Z" />
    <circle cx="6.5" cy="10.5" r="2.3" />
    <circle cx="10.5" cy="8" r="2.3" />
    <circle cx="13.5" cy="8" r="2.3" />
    <circle cx="17.5" cy="10.5" r="2.3" />
  </svg>
);

interface PublicWineListProps {
  userId: string;
  onBack?: () => void;
}

const WineCard: React.FC<{ wine: Wine }> = React.memo(({ wine }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  return (
    <div className="backdrop-blur-sm rounded-lg shadow-md border overflow-hidden flex flex-col h-full" style={{ 
      backgroundColor: 'var(--theme-bg-secondary, #FFFFFF)', 
      borderColor: 'var(--theme-border, #E5E7EB)'
    }}>
      <div className="w-full h-32 sm:h-40 flex items-center justify-center p-2" style={{ backgroundColor: 'var(--theme-bg-primary, #F9FAFB)' }}>
        {wine.image && !imageError ? (
          <>
            {!imageLoaded && (
              <div className="w-12 h-12 sm:w-16 sm:h-16 text-gray-400 opacity-75 animate-pulse">
                <SommelierCatIcon className="w-full h-full" />
              </div>
            )}
            <img 
              src={wine.image} 
              alt={wine.name} 
              className={`max-w-full max-h-full object-contain transition-opacity duration-300 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
              onLoad={() => setImageLoaded(true)}
              onError={() => {
                setImageError(true);
                setImageLoaded(true);
              }}
              loading="lazy"
            />
          </>
        ) : (
          <SommelierCatIcon className="w-12 h-12 sm:w-16 sm:h-16 text-gray-400 opacity-75" />
        )}
      </div>
      <div className="p-3 sm:p-4 flex flex-col flex-grow">
        <div className="flex justify-between items-start flex-wrap gap-x-2 mb-2">
          <h4 className="text-base sm:text-lg font-semibold flex-1" style={{ color: 'var(--theme-text-primary, #1E293B)' }}>{wine.name}</h4>
          <div className="text-right">
            <p className="text-sm sm:text-md font-bold text-red-800 whitespace-nowrap">¥{typeof wine.price === 'number' ? wine.price.toLocaleString() : '0'}</p>
            {wine.isGlassWine && wine.glassPrice && (
              <div className="flex items-center justify-end gap-2 mt-2">
                <span className="text-xs sm:text-sm font-bold text-white bg-gradient-to-r from-blue-500 to-blue-600 px-3 py-1.5 rounded-full shadow-sm">
                  🍷 グラス
                </span>
                <p className="text-base sm:text-lg font-bold text-blue-800 whitespace-nowrap">
                  ¥{typeof wine.glassPrice === 'number' ? wine.glassPrice.toLocaleString() : '0'}
                </p>
              </div>
            )}
          </div>
        </div>
        
        <p className="text-xs sm:text-sm mb-2 sm:mb-3" style={{ color: 'var(--theme-text-secondary, #64748B)' }}>{wine.type} &middot;</p>

        <div className="text-xs sm:text-sm space-y-1 flex-grow" style={{ color: 'var(--theme-text-secondary, #64748B)' }}>
          <div className="flex">
            <span className="w-14 sm:w-16 font-semibold flex-shrink-0" style={{ color: 'var(--theme-text-primary, #1E293B)' }}>産地：</span>
            <span className="flex-1">{wine.origin}</span>
          </div>
          <div className="flex">
            <span className="w-14 sm:w-16 font-semibold flex-shrink-0" style={{ color: 'var(--theme-text-primary, #1E293B)' }}>品種：</span>
            <span className="flex-1">{wine.variety}</span>
          </div>
          <div className="flex">
            <span className="w-14 sm:w-16 font-semibold flex-shrink-0" style={{ color: 'var(--theme-text-primary, #1E293B)' }}>特徴：</span>
            <span className="flex-1">{Array.isArray(wine.characteristics) ? wine.characteristics.join(', ') : String(wine.characteristics || '')}</span>
          </div>
        </div>
      </div>
    </div>
  );
});

// データキャッシュ用のMap
const dataCache = new Map<string, { data: AppData; timestamp: number }>();
const CACHE_DURATION = 5 * 60 * 1000; // 5分間キャッシュ

const PublicWineList: React.FC<PublicWineListProps> = ({ userId, onBack }) => {
  // テーマの状態管理（テーマ変更時に再レンダリングされるようにする）
  const { theme: currentThemeId } = useTheme();
  
  const [wineData, setWineData] = useState<AppData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // AIソムリエ機能の状態管理
  const [showQuestionnaire, setShowQuestionnaire] = useState(false);
  const [showAnalyzing, setShowAnalyzing] = useState(false);
  const [recommendations, setRecommendations] = useState<AiRecommendation[]>([]);
  const [isRecommendationLoading, setIsRecommendationLoading] = useState(false);
  const [isRequestInFlight, setIsRequestInFlight] = useState(false); // 連打防止用
  
  // ワイン表示フィルターの状態管理
  const [wineFilter, setWineFilter] = useState<'all' | 'glass'>('all');
  
  // PDF生成の状態管理
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  
  // QRコード関連の状態管理
  const [qrCodeUrl, setQrCodeUrl] = useState<string>('');
  
  // URLパラメータからフィルターを設定
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const filterParam = urlParams.get('filter');
    
    if (filterParam === 'glass') {
      setWineFilter(filterParam);
    }
  }, []);

  // QRコードを自動生成
  useEffect(() => {
    generateQRCode();
  }, []);

  // QRコード生成
  const generateQRCode = async () => {
    try {
      const currentUrl = window.location.href;
      const qrDataUrl = await QRCode.toDataURL(currentUrl, {
        width: 120,
        margin: 1,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });
      setQrCodeUrl(qrDataUrl);
    } catch (error) {
      // QRコード生成エラーは静かに処理
    }
  };
  
  // ページネーション機能（一時的に無効化）
  // const [currentPage, setCurrentPage] = useState(1);
  // const WINES_PER_PAGE = 12; // 1ページあたりのワイン数

  useEffect(() => {
    const loadWineData = async () => {
      if (!userId) {
        setError('ユーザーIDが指定されていません');
        setLoading(false);
        return;
      }
      
      // キャッシュチェック
      const cached = dataCache.get(userId);
      const now = Date.now();
      if (cached && (now - cached.timestamp) < CACHE_DURATION) {
        setWineData(cached.data);
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        setError(null);
        
        const result = await getAllSyncedData(userId);
        
        if (result.success && result.data) {
          setWineData(result.data);
          // キャッシュに保存
          dataCache.set(userId, { data: result.data, timestamp: Date.now() });
        } else {
          // より詳細なエラーメッセージ
          let errorMessage = 'ワインリストの読み込みに失敗しました';
          if (result.error) {
            if (result.error.includes('not found')) {
              errorMessage = '指定されたユーザーのワインリストが見つかりません';
            } else if (result.error.includes('permission')) {
              errorMessage = 'ワインリストへのアクセス権限がありません';
            } else if (result.error.includes('同期データが見つかりません')) {
              errorMessage = '同期データが見つかりません。管理者がデータを同期してください。';
            } else {
              errorMessage = `エラー: ${String(result.error || '')}`;
            }
          }
          setError(errorMessage);
        }
      } catch (err) {
        setError(`ワインリストの読み込み中にエラーが発生しました: ${String(err || '')}`);
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      loadWineData();
    } else {
      setError('ユーザーIDが提供されていません');
      setLoading(false);
    }
  }, [userId]);

  // AIソムリエ機能のハンドラー（連打防止付き）
  const handleQuestionnaireSubmit = async (answers: UserAnswers) => {
    // 連打防止：既にリクエスト中なら無視
    if (isRequestInFlight || !wineData) return;
    
    setIsRequestInFlight(true);
    
    try {
      // 現在のフィルターに応じてワインリストを決定
      let targetWineList = wineData.wineList;
      if (wineFilter === 'glass') {
        // グラスワインのみを対象にする
        targetWineList = wineData.wineList.filter(wine => wine.isGlassWine === true);
      }
      
      // 質問フォームを閉じて分析中画面を表示
      setShowQuestionnaire(false);
      setShowAnalyzing(true);
      setIsRecommendationLoading(true);
      
      const result = await getWineRecommendation(answers, targetWineList);
      
      if (Array.isArray(result) && result.length > 0) {
        setRecommendations(result);
        setShowAnalyzing(false);
      } else {
        setShowAnalyzing(false);
        const filterMessage = wineFilter === 'glass' ? 'グラスでも飲めるワインの中から' : '';
        alert(`おすすめのワインが見つかりませんでした。${filterMessage}条件を変更してお試しください。`);
      }
    } catch (error) {
      setShowAnalyzing(false);
      alert(`ワイン推薦に失敗しました: ${error instanceof Error ? error.message : '不明なエラー'}`);
    } finally {
      setIsRecommendationLoading(false);
      setIsRequestInFlight(false); // リクエスト完了
    }
  };

  const handleResetRecommendation = () => {
    setRecommendations([]);
    setShowQuestionnaire(false);
    setShowAnalyzing(false);
    setIsRequestInFlight(false); // 連打防止状態もクリア
    // ワインリストのトップにスクロール
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--theme-bg-primary, #FDFBF7)' }}>
        <LoadingSpinner type="loading" title="読み込み中" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--theme-bg-primary, #FDFBF7)' }}>
        <div className="text-center max-w-md mx-auto p-6">
          <div className="text-red-500 text-6xl mb-4">❌</div>
          <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--theme-text-primary, #1E293B)' }}>エラーが発生しました</h2>
          <p className="mb-4" style={{ color: 'var(--theme-text-secondary, #64748B)' }}>{error}</p>
          
          
          <button 
            onClick={() => window.location.reload()} 
            className="bg-red-800 text-white px-6 py-2 rounded-lg hover:bg-red-900 transition-colors"
          >
            再読み込み
          </button>
        </div>
      </div>
    );
  }

  if (!wineData) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--theme-bg-primary, #FDFBF7)' }}>
        <div className="text-center">
          <div className="text-6xl mb-4" style={{ color: 'var(--theme-text-secondary, #9CA3AF)' }}>🍷</div>
          <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--theme-text-primary, #1E293B)' }}>ワインリストが見つかりません</h2>
          <p style={{ color: 'var(--theme-text-secondary, #64748B)' }}>指定されたユーザーのワインリストが存在しません。</p>
        </div>
      </div>
    );
  }

  // FirestoreのデータをWine型に変換
  const convertToWineType = (firestoreWine: any): Wine => {
    return {
      id: firestoreWine.id,
      name: firestoreWine.name,
      type: firestoreWine.type || '赤', // デフォルト値
      origin: firestoreWine.origin,
      variety: firestoreWine.variety || firestoreWine.taste || '不明', // tasteをvarietyにマッピング
      price: typeof firestoreWine.price === 'string' ? 
        parseInt(String(firestoreWine.price || '').replace(/[¥,]/g, '')) || 0 : 
        firestoreWine.price || 0,
      taste: firestoreWine.taste || 'フルボディ', // デフォルト値
      characteristics: firestoreWine.characteristics || [firestoreWine.occasion || 'ディナー'], // occasionをcharacteristicsにマッピング
      image: firestoreWine.image || '',
      visible: firestoreWine.visible !== false,
      // グラスワイン機能
      isGlassWine: firestoreWine.isGlassWine || false,
      glassPrice: firestoreWine.glassPrice || undefined
    };
  };

  // フィルターに応じたワインの絞り込み
  const getFilteredWines = (wines: Wine[]) => {
    switch (wineFilter) {
      case 'glass':
        return wines.filter(wine => wine.isGlassWine === true);
      case 'all':
      default:
        return wines;
    }
  };

  const visibleWines = wineData.wineList
    .filter(wine => wine.visible)
    .map(convertToWineType);

  const filteredWines = getFilteredWines(visibleWines);

  // メインのWineMenuと同じソート機能（フィルター適用後）
  const winesByType = filteredWines.reduce((acc, wine) => {
    const type = wine.type;
    if (!acc[type]) {
      acc[type] = [];
    }
    acc[type].push(wine);
    return acc;
  }, {} as Record<Wine['type'], Wine[]>);

  // 各ジャンル内で価格順（安い順）にソート
  Object.keys(winesByType).forEach(type => {
    winesByType[type as Wine['type']].sort((a, b) => a.price - b.price);
  });

  const typeOrder: Wine['type'][] = ['スパークリング', 'ロゼ', '白', '赤'];
  
  // ページネーション用の計算（一時的に無効化）
  // const allWines = typeOrder.flatMap(type => winesByType[type] || []);
  // const totalPages = Math.ceil(allWines.length / WINES_PER_PAGE);
  // const startIndex = (currentPage - 1) * WINES_PER_PAGE;
  // const endIndex = startIndex + WINES_PER_PAGE;
  // const currentWines = allWines.slice(startIndex, endIndex);
  
  // ページネーション用のワインをタイプ別に再グループ化（一時的に無効化）
  // const paginatedWinesByType = currentWines.reduce((acc, wine) => {
  //   const type = wine.type;
  //   if (!acc[type]) {
  //     acc[type] = [];
  //   }
  //   acc[type].push(wine);
  //   return acc;
  // }, {} as Record<Wine['type'], Wine[]>);

  // PDFダウンロードハンドラー
  const handleDownloadPdf = async () => {
    if (!wineData || filteredWines.length === 0) {
      alert('ワインリストが空です');
      return;
    }

    setIsGeneratingPdf(true);
    try {
      const shopName = wineData.shopProfile?.name || 'ワインリスト';
      const isGlassList = wineFilter === 'glass';
      const filename = isGlassList
        ? `${shopName}_TODAYS_GLASS_${new Date().toISOString().split('T')[0]}.pdf`
        : `${shopName}_ワインリスト_${new Date().toISOString().split('T')[0]}.pdf`;
      const pdfUrl = await generateWineListPdf(
        filteredWines,
        shopName,
        isGlassList ? glassPdfStyle : defaultPdfStyle
      );

      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(pdfUrl);
    } catch (error) {
      console.error('PDF生成エラー:', error);
      alert(`PDFの生成に失敗しました: ${error instanceof Error ? error.message : '不明なエラー'}`);
    } finally {
      setIsGeneratingPdf(false);
    }
  };


  // AIソムリエ機能の表示
  if (showAnalyzing && isRecommendationLoading) {
    return <LoadingSpinner type="analyzing" title="分析中" showAdvancedUI={true} />;
  }

  if (showQuestionnaire) {
    if (!wineData) {
      return (
        <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--theme-bg-primary, #FDFBF7)' }}>
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4"></div>
            <p style={{ color: 'var(--theme-text-secondary, #64748B)' }}>データを読み込み中...</p>
          </div>
        </div>
      );
    }
    
    // グラスワインフィルターの場合は専用の質問ページを表示
    if (wineFilter === 'glass') {
      return (
        <div className="min-h-screen" style={{ backgroundColor: 'var(--theme-bg-primary, #FDFBF7)' }}>
          <div className="w-full max-w-4xl mx-auto p-4 flex justify-center">
            <GlassWineQuestionnaire 
              onSubmit={handleQuestionnaireSubmit} 
              onBack={() => setShowQuestionnaire(false)}
              occasions={wineData.occasions || []}
              tastes={wineData.tastes || []}
              origins={wineData.origins || []}
            />
          </div>
        </div>
      );
    }
    
    // 通常の質問ページ
    return (
      <div className="min-h-screen" style={{ backgroundColor: 'var(--theme-bg-primary, #FDFBF7)' }}>
        <div className="w-full max-w-4xl mx-auto p-4">
          <Questionnaire 
            onSubmit={handleQuestionnaireSubmit} 
            onBack={() => {
              console.log('PublicWineList: 戻るボタンがクリックされました');
              setShowQuestionnaire(false);
            }}
            initialAnswers={null}
            error={null}
            wineList={wineData.wineList || []}
            occasions={wineData.occasions || []}
            tastes={wineData.tastes || []}
            prices={wineData.prices || []}
            origins={wineData.origins || []}
          />
        </div>
      </div>
    );
  }

  if (recommendations.length > 0) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: 'var(--theme-bg-primary, #FDFBF7)' }}>
        <div className="w-full max-w-4xl mx-auto p-4">
          <Recommendation 
            recommendations={recommendations} 
            wineList={wineData?.wineList || []}
            onReset={handleResetRecommendation} 
          />
        </div>
      </div>
    );
  }

  if (isRecommendationLoading) {
    return <LoadingSpinner type="analyzing" title="分析中" showAdvancedUI={true} />;
  }


  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--theme-bg-primary, #FDFBF7)' }}>
      {/* 通常表示用のコンテンツ */}
      <div className="w-full max-w-4xl mx-auto p-4">
        {/* ヘッダー - メインのWineMenuと同じスタイル */}
        <div className="text-center mb-6 sm:mb-10">
          <div className="flex flex-col sm:flex-row justify-center items-center gap-3 sm:gap-4 mb-2">
            {wineData.shopProfile.logo && (
              <img 
                src={wineData.shopProfile.logo} 
                alt="Shop Logo" 
                className="h-12 sm:h-16 w-auto"
              />
            )}
            <div className="flex items-center justify-center gap-4">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold" style={{ color: 'var(--theme-text-primary, #1E293B)' }}>
                {wineData.shopProfile.name || 'Wine List'}
              </h2>
              {qrCodeUrl && (
                <img 
                  src={qrCodeUrl} 
                  alt="QR Code" 
                  className="w-16 h-16 sm:w-20 sm:h-20"
                />
              )}
            </div>
          </div>
          <p className="text-base sm:text-lg" style={{ color: 'var(--theme-text-secondary, #64748B)' }}>当店のワインリストです。ごゆっくりお選びください。</p>
          
          
          {/* ワイン表示フィルター */}
          <div className="mt-6">
            <div className="flex justify-center mb-4">
              <div className="backdrop-blur-sm rounded-lg p-1 shadow-md border" style={{ backgroundColor: 'var(--theme-bg-secondary, #FFFFFF)', opacity: 0.8, borderColor: 'var(--theme-border, #E5E7EB)' }}>
                <div className="flex space-x-1">
                  <button
                    onClick={() => setWineFilter('all')}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                      wineFilter === 'all'
                        ? 'bg-red-800 text-white shadow-sm'
                        : 'hover:bg-gray-100'
                    }`}
                    style={wineFilter !== 'all' ? { color: 'var(--theme-text-secondary, #64748B)' } : {}}
                  >
                    全てのワイン
                  </button>
                  <button
                    onClick={() => setWineFilter('glass')}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                      wineFilter === 'glass'
                        ? 'bg-red-800 text-white shadow-sm'
                        : 'hover:bg-gray-100'
                    }`}
                    style={wineFilter !== 'glass' ? { color: 'var(--theme-text-secondary, #64748B)' } : {}}
                  >
                    🍷 グラスでも飲めるワイン
                  </button>
                </div>
              </div>
            </div>
            
            {/* フィルター状態とワイン数表示 */}
            <div className="text-center mb-4">
              <p className="text-sm" style={{ color: 'var(--theme-text-secondary, #64748B)' }}>
                {wineFilter === 'all' && `全 ${filteredWines.length} 本のワイン`}
                {wineFilter === 'glass' && `グラスでも飲めるワイン ${filteredWines.length} 本`}
              </p>
            </div>
          </div>
          
          {/* AIソムリエ機能ボタンとPDFダウンロードボタン */}
          <div className="text-center mt-6 space-y-4">
            <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
              <button
                onClick={() => setShowQuestionnaire(true)}
                className="bg-red-800 hover:bg-red-900 text-white font-bold py-3 px-6 rounded-lg shadow-md transition-colors duration-300 flex items-center gap-2"
              >
                <SommelierCatIcon className="w-6 h-6" />
                <span>
                  AIソムリエに相談する
                  {wineFilter === 'glass' && '（グラスワインから選ぶ）'}
                </span>
              </button>
              
              {filteredWines.length > 0 && (
                <button
                  type="button"
                  onClick={handleDownloadPdf}
                  disabled={isGeneratingPdf}
                  className="bg-white border-2 border-red-800 text-red-800 hover:bg-red-50 font-bold py-3 px-6 rounded-lg shadow-md transition-colors duration-300 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <PrintIcon />
                  <span>{isGeneratingPdf ? 'PDF生成中...' : 'PDFでダウンロード'}</span>
                </button>
              )}
            </div>
            {wineFilter === 'glass' && (
              <p className="text-sm mt-2" style={{ color: 'var(--theme-text-secondary, #64748B)' }}>
                グラスでも飲めるワインの中からおすすめを選びます
              </p>
            )}
          </div>
        </div>

        {/* ワインリスト - ジャンル別表示 */}
        {filteredWines.length > 0 ? (
          <div className="space-y-8 sm:space-y-12">
            {typeOrder.map(type => (
              winesByType[type] && winesByType[type].length > 0 && (
                <section key={type} aria-labelledby={`wine-type-${type}`}>
                  <h3 id={`wine-type-${type}`} className="text-xl sm:text-2xl font-bold text-red-900/80 border-b-2 border-red-800/20 pb-2 mb-4 sm:mb-6">
                    {type}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                    {winesByType[type].map(wine => (
                      <WineCard key={wine.id} wine={wine} />
                    ))}
                  </div>
                </section>
              )
            ))}
          </div>
        ) : (
          <div className="text-center p-4 sm:p-8 rounded-lg" style={{ backgroundColor: 'var(--theme-bg-secondary, #FFFFFF)', opacity: 0.5 }}>
            <div className="text-gray-400 text-6xl mb-4">
              {wineFilter === 'glass' ? '🍷' : '🍷'}
            </div>
            <p className="text-lg" style={{ color: 'var(--theme-text-secondary, #6B7280)' }}>
              {wineFilter === 'all' && 'ワインリストは空です。'}
              {wineFilter === 'glass' && 'グラスでも飲めるワインはありません。'}
            </p>
            {wineFilter !== 'all' && (
              <p className="text-sm mt-2" style={{ color: 'var(--theme-text-secondary, #9CA3AF)' }}>
                他のフィルターを試してみてください。
              </p>
            )}
          </div>
        )}


        {/* フッター - メインのWineMenuと同じスタイル */}
        <div className="text-center mt-8 text-sm" style={{ color: 'var(--theme-text-secondary, #6B7280)' }}>
          <p>最終更新: {new Date().toLocaleString('ja-JP')}</p>
          <p className="mt-2">
            <span style={{ color: 'var(--theme-text-secondary, #9CA3AF)' }}>Powered by</span> 
            <span className="ml-1 font-semibold text-red-600">Orange Concept Sommelier Lab</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PublicWineList;
